/**
 * @mhmo91/ican — runtime AST evaluator.
 *
 * Walks a frozen `Cond` against a real `(data, me)` pair. The AST is
 * built once by the recording proxy at module load and re-walked on
 * every `me.can(...)` call — no Proxy on this path, no allocation.
 *
 * Default-deny + deny-overrides is implemented at the rule level by
 * `me.can`; this module evaluates one `Cond` to a boolean.
 *
 * ── KERNEL CONTRACT (active-workspace UI predictor) ──────────────────────────
 *
 * The kernel (`me.can`) answers for the actor's ACTIVE workspace.
 * It is a UI-affordance predictor, not a security boundary.
 *
 * Concretely:
 *  - `isMember` resolves from `actor.workspaces[actor[wsIdVar]]` — the
 *    workspace the actor is currently operating in, pre-populated at the
 *    session boundary by the FE (app-shell.sync) from the full
 *    grants collection.
 *  - `hasPermission` resolves from `actor.permissions`, assembled for
 *    that same active workspace. A `hasPermission(…, { workspace })` leaf
 *    names its workspace outright and answers from those permissions only
 *    while the active workspace — `actor.orgId`, else `actor.workspaceId` —
 *    is that id; for an actor active anywhere else it is false, since the
 *    permissions it holds
 *    speak for the active workspace alone.
 *  - Implicit permission leaves use bound organization path variables:
 *    an active owner in A cannot borrow that power for a B target. Membership
 *    predicates resolve the actual bound path before the active context.
 *  - Explicit event permission leaves also accept the active event projection,
 *    only while that projection matches the target event. Organization-only
 *    leaves read organization permissions exclusively.
 */
import { scopeContains } from './permissions.ts';
import type { AccessContext, Cond, FieldPath, Literal } from './types.ts';

const resolve = (path: FieldPath, data: unknown, me: unknown, context?: AccessContext): unknown => {
  const [source, ...keys] = path;
  // Bound callers supply real path variables explicitly; absent context fails closed.
  if (source === 'pathVar') {
    const value = context?.pathVars?.[keys[0] ?? ''];
    return keys.length === 1 ? value : value?.split('/')[Number(keys[1])];
  }
  let current: unknown = source === 'data' ? data : source === 'parent' ? context?.parents : me;
  for (const key of keys) {
    if (current === null || current === undefined) return undefined;
    current = (current as Record<string, unknown>)[key];
  }
  return current;
};

const rhs = (value: Literal | FieldPath, data: unknown, me: unknown, context?: AccessContext): unknown => {
  if (Array.isArray(value)) return resolve(value as FieldPath, data, me, context);
  return value;
};

const compare = (
  op: 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte',
  left: unknown,
  right: unknown,
): boolean => {
  switch (op) {
    case 'eq':
      return left === right;
    case 'ne':
      // An absent left field cannot satisfy `!=` in the emitted rule: the
      // Firestore expression `resource.data.deletedAt != "DELETED"` references
      // an absent field → evaluation error → default-DENY. Guard the same
      // deny direction here so an absent left (undefined/null) returns FALSE
      // rather than the JS-native `undefined !== 'DELETED'` = true (GRANT).
      // KNOWN LIMITATION (active-workspace predictor): `not(eq(absentField))`
      // still returns true in the kernel (eq → false, not → true) while
      // Firestore denies. A three-valued-logic rewrite that threads
      // field-presence through `not` is out of scope; the residual over-grant
      // is confined to the `not(eq(...))` shape on an absent field.
      if (left === undefined || left === null) return false;
      return left !== right;
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      // Ordering against an absent field is always false — a missing
      // claim shouldn't accidentally satisfy `me.tier.gte(3)`.
      if (left === undefined || left === null || right === undefined || right === null) {
        return false;
      }
      const l = left as number;
      const r = right as number;
      if (op === 'gt') return l > r;
      if (op === 'gte') return l >= r;
      if (op === 'lt') return l < r;
      return l <= r;
    }
  }
};

export const evaluate = (cond: Cond, data: unknown, me: unknown, context?: AccessContext): boolean => {
  // `me.can(...)` receives one document and an actor, never that document's
  // ancestor, so a workspace-document read is undecidable here. It declines to
  // decide rather than answering false: false would make a rule whose only
  // public arm is the parent gate deny every affordance the deployed rule
  // grants, and this kernel is an affordance predictor whose security boundary
  // is the emitted rule — the same split the write pattern already runs on.
  // Every FieldPath a leaf can carry is checked, not only `left`, because
  // `resolve` knows only `data` and `me` and would read a workspace path off
  // the actor. KNOWN LIMITATION: `not(workspace<W>().x.eq(v))` inverts to
  // false here while Firestore may allow.
  if (
    ('left' in cond && cond.left[0] === 'workspace') ||
    (cond.kind === 'cmp' && Array.isArray(cond.right) && cond.right[0] === 'workspace') ||
    (cond.kind === 'containsMember' && cond.actorRef[0] === 'workspace')
  ) {
    return true;
  }
  switch (cond.kind) {
    case 'dataExists':
      return data !== null && data !== undefined;
    case 'parentAccess': {
      const parent = context?.parents?.[cond.parent];
      if (parent === null || typeof parent !== 'object' || Array.isArray(parent)) return false;
      let allowed = false;
      for (const rule of cond.rules) {
        if (!evaluate(rule.cond, parent, me, context)) continue;
        if (rule.effect === 'deny') return false;
        allowed = true;
      }
      return allowed;
    }
    case 'exists': {
      const [source, ...keys] = cond.left;
      if (source === 'pathVar') return resolve(cond.left, data, me, context) !== undefined;
      if (keys.length === 0) return false;
      let current = source === 'data' ? data : source === 'parent' ? context?.parents : me;
      for (const key of keys) {
        if (
          current === null ||
          typeof current !== 'object' ||
          Array.isArray(current) ||
          !Object.hasOwn(current, key)
        ) {
          return false;
        }
        const prototype = Object.getPrototypeOf(current);
        if (prototype !== null && prototype !== Object.prototype) return false;
        current = Reflect.get(current, key);
      }
      return true;
    }
    case 'cmp': {
      const left = resolve(cond.left, data, me, context);
      const right = rhs(cond.right, data, me, context);
      // `ne` with a FieldPath right operand that resolves to an absent field
      // must ALSO deny, mirroring Firestore's `resource.data.x != resource.data.y`
      // erroring (default-DENY) when `y` is absent. `Array.isArray(cond.right)`
      // distinguishes this from a LITERAL `null` right operand (`data.x.ne(null)`),
      // which is not a FieldPath and must keep meaning "x is not null" — granting
      // when x is present. Checked here, before `compare`, because `compare` only
      // ever sees resolved values and cannot tell a resolved-to-null field apart
      // from an authored literal null.
      if (
        cond.op === 'ne' &&
        Array.isArray(cond.right) &&
        (right === undefined || right === null)
      ) {
        return false;
      }
      return compare(cond.op, left, right);
    }
    case 'in': {
      const left = resolve(cond.left, data, me, context);
      return cond.values.includes(left as Literal);
    }
    case 'hasAny': {
      const left = resolve(cond.left, data, me, context);
      if (!Array.isArray(left)) return false;
      return cond.values.some((v) => left.includes(v));
    }
    case 'hasAll': {
      const left = resolve(cond.left, data, me, context);
      if (!Array.isArray(left)) return false;
      return cond.values.every((v) => left.includes(v));
    }
    case 'hasOnly': {
      const left = resolve(cond.left, data, me, context);
      if (!Array.isArray(left)) return false;
      return left.every((v) => cond.values.includes(v as Literal));
    }
    case 'matches': {
      const left = resolve(cond.left, data, me, context);
      if (typeof left !== 'string') return false;
      // Anchor to full-string to match Firestore RE2 `string.matches()` semantics,
      // which always anchors the pattern to the full string. JS `.test()` is an
      // unanchored substring match — wrapping with `^(?:...)$` aligns the runtime
      // with the emitted Firestore rule (no change to the emitted `.matches()` call).
      // The author regex is compiled by the JS engine on every evaluate. A pattern
      // valid in Firestore's RE2 but invalid in ECMAScript (e.g. `(?P<name>...)`
      // named groups) throws a SyntaxError from `new RegExp`; without this guard it
      // unwinds out of `me.can` (actor.ts has no try/catch), crashing the FE
      // affordance check or server gate instead of returning a boolean. Fail
      // closed: an uncompilable pattern denies.
      // KNOWN LIMITATION (active-workspace predictor): an RE2-only regex that JS
      // cannot compile makes the kernel DENY while Firestore may ALLOW — the safe
      // direction (deny rather than crash-or-over-grant).
      try {
        return new RegExp(`^(?:${cond.regex})$`).test(left);
      } catch {
        return false;
      }
    }
    case 'containsMember': {
      const arr = resolve(cond.left, data, me, context);
      if (!Array.isArray(arr)) return false;
      const member = resolve(cond.actorRef, data, me, context);
      if (member === undefined || member === null) return false;
      return arr.includes(member);
    }
    case 'hasPermission': {
      const targetOrg = context?.pathVars?.orgId ?? context?.pathVars?.workspaceId;
      const actorOrg = Reflect.get(Object(me), 'orgId') ?? Reflect.get(Object(me), 'workspaceId');
      // Explicit permission authority may name a different workspace than the resource path.
      if (
        cond.workspace === undefined && cond.workspaceField === undefined &&
        targetOrg !== undefined && targetOrg !== actorOrg
      ) return false;
      if (cond.workspace !== undefined && actorOrg !== cond.workspace) return false;
      // `workspaceField` names the workspace on the DOCUMENT. `me.permissions`
      // speaks for the actor's ACTIVE workspace alone (the KERNEL CONTRACT at
      // the head of this file), so those permissions answer for this document
      // only while the two are the same workspace — for any other, the actor
      // holds no permissions this leaf can read and the honest answer is false.
      if (cond.workspaceField !== undefined) {
        const docWsId = typeof cond.workspaceField === 'string'
          ? (data as Record<string, unknown> | undefined)?.[cond.workspaceField]
          : resolve(cond.workspaceField, data, me, context);
        if (typeof docWsId !== 'string' || docWsId.length === 0) return false;
        if (actorOrg !== docWsId) return false;
      }
      const orgPerms = resolve(cond.permsField, data, me, context);
      const eventId = cond.eventField === undefined ? undefined : resolve(cond.eventField, data, me, context) ?? (context?.pathVars === undefined ? Reflect.get(Object(me), cond.eventField[1] ?? '') : undefined);
      const eventPerms = eventId !== undefined && eventId === Reflect.get(Object(me), 'eventId') ? Reflect.get(Object(me), 'eventPermissions') : undefined;
      const perms = cond.eventField !== undefined && Array.isArray(eventPerms) && Array.isArray(orgPerms) ? [...orgPerms, ...eventPerms] : orgPerms;
      if (!Array.isArray(perms)) return false;
      // Inline wildcard expansion — must mirror expandPermission().
      const lastDot = cond.permission.lastIndexOf('.');
      const tokens =
        lastDot === -1
          ? [cond.permission, '*']
          : [cond.permission, `${cond.permission.slice(0, lastDot)}.*`, '*'];
      if (!tokens.some((t) => perms.includes(t))) return false;
      if (cond.scope === undefined) return true;
      if (cond.scope.idField !== undefined) {
        const id = cond.scope.idField[0] === 'pathVar'
          ? resolve(cond.scope.idField, data, me, context) ?? (context?.pathVars === undefined ? Reflect.get(Object(me), cond.scope.idField[1] ?? '') : undefined)
          : resolve(cond.scope.idField, data, me, context);
        return typeof id === 'string' && id.length > 0 &&
          scopeContains(Reflect.get(Object(me), cond.scope.roleField), [id], cond.scope.mode);
      }
      if (cond.scope.keysField !== undefined) {
        const map = resolve(cond.scope.keysField, data, me, context);
        return map !== null && typeof map === 'object' && !Array.isArray(map) &&
          scopeContains(Reflect.get(Object(me), cond.scope.roleField), Object.keys(map), cond.scope.mode);
      }
      const required =
        cond.scope.idsField !== undefined ? resolve(cond.scope.idsField, data, me, context) : undefined;
      return scopeContains(Reflect.get(Object(me), cond.scope.roleField), required, cond.scope.mode);
    }
    case 'pathOwner': {
      const uid = Reflect.get(Object(me), 'uid');
      return (
        typeof uid === 'string' &&
        uid.length > 0 &&
        context?.pathVars?.[cond.variable] === `${cond.prefix}${uid}`
      );
    }
    case 'isMember': {
      if (cond.envelope === true) {
        const target = context?.pathVars?.[cond.wsIdVar] ?? Reflect.get(Object(me), cond.wsIdVar);
        const memberships = Reflect.get(Object(me), 'memberships');
        return Array.isArray(memberships) && memberships.includes(target);
      }
      // Runtime membership check: the actor carries `workspaces: { [wsId]: roleId }`.
      // The workspaceId value must be on the actor (built at the session boundary);
      // pathVar refs always resolve to undefined so membership is false outside Firestore.
      const actor = me as Record<string, unknown>;
      const workspaces = actor.workspaces;
      if (workspaces === null || typeof workspaces !== 'object') return false;
      // The wsIdVar encodes the variable name used in the entity path (e.g. 'workspaceId').
      // The runtime resolves the actual workspace id from the actor field of the same name.
      // memberOf(field) instead names a field of the DOCUMENT, mirroring the
      // emitted `resource.data.<field>` the rule keys its grant lookup on.
      const wsId =
        cond.wsIdField !== undefined
          ? (data as Record<string, unknown> | undefined)?.[cond.wsIdField]
          : context?.pathVars?.[cond.wsIdVar] ?? actor[cond.wsIdVar];
      if (typeof wsId !== 'string' || wsId.length === 0) return false;
      const roleId = (workspaces as Record<string, unknown>)[wsId];
      return roleId !== null && roleId !== undefined;
    }
    case 'ownRole': {
      // Mirrors isMember's actor.workspaces lookup, then compares the
      // resolved roleId against idField (typically data.id) instead of
      // merely asserting it is non-null.
      const actor = me as Record<string, unknown>;
      const workspaces = actor.workspaces;
      if (workspaces === null || typeof workspaces !== 'object') return false;
      const wsId = context?.pathVars?.[cond.wsIdVar] ?? actor[cond.wsIdVar];
      if (typeof wsId !== 'string' || wsId.length === 0) return false;
      const roleId = (workspaces as Record<string, unknown>)[wsId];
      if (typeof roleId !== 'string') return false;
      return roleId === resolve(cond.idField, data, me, context);
    }
    case 'and':
      return cond.nodes.every((n) => evaluate(n, data, me, context));
    case 'or':
      return cond.nodes.some((n) => evaluate(n, data, me, context));
    case 'not':
      return !evaluate(cond.node, data, me, context);
  }
};
